import { types, Instance, flow, getRoot } from 'mobx-state-tree';

import {
  ApiWorkout,
  CardPhases,
  cardPhaseKeys,
  CreateWorkoutPayload,
  RemoteConfigData,
} from './types';
import type {
  IWorkoutModel,
  IWorkoutSegmentModel,
} from '@/stores/models/WorkoutModel';
import {
  startWorkout as apiStartWorkout,
  updateSegment as apiUpdateSegment,
  completeWorkout as apiCompleteWorkout,
  createWorkout as apiCreateWorkout,
} from '@/services/api/workoutApi';
import { resolveWorkout } from '@/services/workoutResolver';
import remoteConfigData from '@/data/remoteConfig';
import { nowMs } from '@/services/datetime';

export type IWorkoutPlayerStore = Instance<typeof WorkoutPlayerStore>;

const WorkoutPlayerStore = types
  .model('WorkoutPlayerStore', {
    cardPhase: types.optional(
      types.enumeration('CardPhase', cardPhaseKeys),
      CardPhases.DISCARD,
    ),
    isPaused: false,
    currentSegmentIndex: 0,
    currentVideoIndex: 0,
    repCount: 0,
    didSkipSegment: false,

    // Timer state (inline for POC simplicity)
    timerRemainingMs: 0,
    timerDurationMs: 0,
    timerRunning: false,

    // Workout timestamps
    workoutStartedAt: types.maybeNull(types.number),
    workoutCompletedAt: types.maybeNull(types.number),
  })
  .volatile(() => ({
    timerIntervalId: null as ReturnType<typeof setInterval> | null,
    workout: null as IWorkoutModel | null,
    // Loading state for createAndLoadWorkout
    isCreatingWorkout: false,
    createError: null as string | null,
  }))
  .views((self) => ({
    get currentSegment(): IWorkoutSegmentModel | null {
      if (!self.workout) return null;
      return self.workout.segmentAtIndex(self.currentSegmentIndex);
    },

    get currentVideoUrl(): string | null {
      const segment = this.currentSegment;
      if (!segment) return null;
      if (segment.segment_info.videoUrls.length === 0) return null;
      const idx = Math.min(
        self.currentVideoIndex,
        segment.segment_info.videoUrls.length - 1,
      );
      return segment.segment_info.videoUrls[idx];
    },

    get isRestSegment(): boolean {
      return this.currentSegment?.isRest ?? false;
    },

    get isExerciseSegment(): boolean {
      return this.currentSegment?.isExercise ?? false;
    },

    get isPlayStep(): boolean {
      return (
        self.cardPhase === CardPhases.PLAY_INTRO ||
        self.cardPhase === CardPhases.PLAY_ACTIVE ||
        self.cardPhase === CardPhases.PLAY_OUTRO
      );
    },

    get isDrawStep(): boolean {
      return (
        self.cardPhase === CardPhases.DRAW_INTRO ||
        self.cardPhase === CardPhases.DRAW_ACTIVE ||
        self.cardPhase === CardPhases.DRAW_OUTRO
      );
    },

    get isChangePhase(): boolean {
      return (
        self.cardPhase === CardPhases.CHANGE_INTRO ||
        self.cardPhase === CardPhases.CHANGE_ACTIVE ||
        self.cardPhase === CardPhases.CHANGE_OUTRO
      );
    },

    get isExercisePlayStep(): boolean {
      return this.isExerciseSegment && this.isPlayStep;
    },

    get isFinished(): boolean {
      return self.cardPhase === CardPhases.FINISHED;
    },

    get hasCompletedAllVideos(): boolean {
      const segment = this.currentSegment;
      if (!segment) return true;
      return (
        self.currentVideoIndex >= segment.segment_info.videoUrls.length - 1
      );
    },

    get segmentLabel(): string | null {
      return this.currentSegment?.segment_info.name ?? null;
    },

    get segmentTypeLabel(): string | null {
      return this.currentSegment?.segment_info.segment_type ?? null;
    },

    get groupLabel(): string | null {
      return this.currentSegment?.group ?? null;
    },

    get progress(): string {
      if (!self.workout) return '';
      const segment = this.currentSegment;
      if (!segment) return '';

      const group = segment.group;
      const segmentsInGroup = self.workout.segments.filter(
        (s) => s.group === group,
      );
      const posInGroup = segmentsInGroup.indexOf(segment) + 1;
      return `${group} ${posInGroup}/${segmentsInGroup.length}`;
    },

    get repsOrTimeLabel(): string | null {
      if (this.isExercisePlayStep) {
        return self.repCount.toString();
      }

      if (self.timerRunning || self.timerRemainingMs > 0) {
        const totalSeconds = Math.ceil(self.timerRemainingMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes > 0) {
          return `${minutes}:${String(seconds).padStart(2, '0')}`;
        }
        return `${seconds}`;
      }

      return null;
    },

    get nextUpTitle(): string | null {
      if (self.cardPhase === CardPhases.DRAW_ACTIVE) {
        if (this.isRestSegment) return 'Hydration Break';
        if (!this.isExerciseSegment) return 'Next';
        return 'Next Up';
      }
      if (this.isRestSegment && this.isPlayStep) {
        return 'Hydration Break';
      }
      return null;
    },
  }))
  .actions((self) => {
    // Helper: call the action-wrapped nextPhase via self (safe from setTimeout/setInterval).
    // Skips if paused — togglePause on resume re-kicks deferred transitions.
    function deferNextPhase(delay: number) {
      setTimeout(() => {
        if (self.isPaused) return;
        (self as IWorkoutPlayerStore).nextPhase();
      }, delay);
    }

    function clearTimer() {
      if (self.timerIntervalId) {
        clearInterval(self.timerIntervalId);
        self.timerIntervalId = null;
      }
      self.timerRunning = false;
    }

    function stopTimer() {
      clearTimer();
      self.timerDurationMs = 0;
      self.timerRemainingMs = 0;
    }

    function startTimerInterval() {
      self.timerIntervalId = setInterval(() => {
        // Route through the action-wrapped _timerTick
        (self as IWorkoutPlayerStore)._timerTick();
      }, 1000);
    }

    function startTimer(durationMs: number) {
      if (!durationMs) return;

      stopTimer();
      self.timerDurationMs = durationMs;
      self.timerRemainingMs = durationMs;
      self.timerRunning = true;
      startTimerInterval();
    }

    function pauseTimer() {
      if (!self.timerRunning) return;
      clearTimer();
    }

    function resumeTimer() {
      if (self.timerRunning) return;
      if (self.timerRemainingMs <= 0) return;

      self.timerRunning = true;
      startTimerInterval();
    }

    // Side effects that must run whenever the workout transitions paused -> playing.
    // Shared by togglePause() (user-driven) and startPlayback() (countdown-driven)
    // so both paths resume the timer and re-kick any phase transition that was
    // deferred while paused.
    function applyUnpause() {
      resumeTimer();

      // Re-kick deferred transitions that were skipped while paused
      const transitionalPhases = [
        CardPhases.DRAW_INTRO,
        CardPhases.DRAW_OUTRO,
        CardPhases.PLAY_INTRO,
        CardPhases.PLAY_OUTRO,
        CardPhases.CHANGE_INTRO,
        CardPhases.CHANGE_OUTRO,
        CardPhases.DISCARD,
      ];
      if (transitionalPhases.includes(self.cardPhase as CardPhases)) {
        deferNextPhase(100);
      }
    }

    function setNextCardPhase(didSkip: boolean) {
      const segment = self.currentSegment;
      if (!segment) {
        console.warn('[Store] setNextCardPhase: no current segment');
        return;
      }

      // Stop any running timer before transitioning
      stopTimer();

      switch (self.cardPhase) {
        case CardPhases.DRAW_INTRO:
          self.didSkipSegment = false;
          self.cardPhase = CardPhases.DRAW_ACTIVE;

          // Start preview timer for non-rest segments
          if (!segment.isRest && segment.segment_info.preview_time_ms > 0) {
            startTimer(segment.segment_info.preview_time_ms);
          } else {
            // No preview — immediately move to DRAW_OUTRO
            deferNextPhase(100);
          }
          return;

        case CardPhases.DRAW_ACTIVE:
          self.cardPhase = CardPhases.DRAW_OUTRO;
          // Brief pause in DRAW_OUTRO then auto-advance
          deferNextPhase(300);
          return;

        case CardPhases.DRAW_OUTRO:
          if (segment.isExercise && segment.repetitions) {
            self.repCount = segment.repetitions;
          }

          self.cardPhase = CardPhases.PLAY_INTRO;
          // Brief transition then auto-advance to PLAY_ACTIVE
          deferNextPhase(300);
          return;

        case CardPhases.PLAY_INTRO:
          // Start timer for timed segments (rest/warmup/cooldown/recover)
          if (segment.isTimedSegment) {
            if (segment.segment_info.completion_time_ms > 0) {
              startTimer(segment.segment_info.completion_time_ms);
            }
          }

          self.cardPhase = CardPhases.PLAY_ACTIVE;
          return;

        case CardPhases.PLAY_ACTIVE:
          self.didSkipSegment = didSkip;

          if (didSkip) {
            self.cardPhase = CardPhases.PLAY_OUTRO;
            // Auto-advance through outro
            deferNextPhase(300);
            return;
          }

          // Check if bilateral exercise with more videos to play
          if (!segment.isRest && !self.hasCompletedAllVideos) {
            self.cardPhase = CardPhases.CHANGE_INTRO;
            // Auto-advance through CHANGE_INTRO
            deferNextPhase(300);
            return;
          }

          self.cardPhase = CardPhases.PLAY_OUTRO;
          // Auto-advance through outro
          deferNextPhase(300);
          return;

        case CardPhases.CHANGE_INTRO:
          self.cardPhase = CardPhases.CHANGE_ACTIVE;
          // 4 second "switch sides" timer
          startTimer(4000);
          return;

        case CardPhases.CHANGE_ACTIVE:
          // Advance to next video
          self.currentVideoIndex += 1;

          // Reset rep count for the next side
          if (segment.isExercise && segment.repetitions) {
            self.repCount = segment.repetitions;
          }

          self.cardPhase = CardPhases.CHANGE_OUTRO;
          // Brief pause then back to play
          deferNextPhase(300);
          return;

        case CardPhases.CHANGE_OUTRO:
          self.cardPhase = CardPhases.PLAY_ACTIVE;
          return;

        case CardPhases.PLAY_OUTRO:
          // Complete the current segment and move to next
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          completeCurrentSegment(self.didSkipSegment);

          // Advance to next segment
          const nextIndex = self.currentSegmentIndex + 1;
          if (self.workout && nextIndex < self.workout.segments.length) {
            self.currentSegmentIndex = nextIndex;
            self.currentVideoIndex = 0;
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            startCurrentSegment();
            self.cardPhase = CardPhases.DISCARD;
            // Auto-advance from DISCARD to DRAW_INTRO
            deferNextPhase(300);
          } else {
            // No more segments — end workout
            self.cardPhase = CardPhases.DISCARD;
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            endWorkout();
          }
          return;

        case CardPhases.DISCARD:
          if (self.workoutCompletedAt) {
            self.cardPhase = CardPhases.FINISHED;
            return;
          }

          self.cardPhase = CardPhases.DRAW_INTRO;
          // Auto-advance from DRAW_INTRO
          deferNextPhase(100);
          return;

        default:
          break;
      }
    }

    function nextPhase(didSkip: boolean = false) {
      const segment = self.currentSegment;
      console.log(
        `[Store] nextPhase: ${self.cardPhase} | segment: ${segment?.segment_info.name} | type: ${segment?.segment_info.segment_type}`,
      );

      setNextCardPhase(didSkip);
    }

    function startCurrentSegment() {
      const segment = self.currentSegment;
      if (!segment || !self.workout) return;

      segment.markStarted();

      apiUpdateSegment(self.workout.id, segment.id, {
        started_at: segment.started_at,
      });
    }

    function completeCurrentSegment(hasSkipped: boolean) {
      const segment = self.currentSegment;
      if (!segment || !self.workout) return;

      segment.markCompleted(hasSkipped);

      apiUpdateSegment(self.workout.id, segment.id, {
        has_skipped: hasSkipped,
        started_at: segment.started_at,
        completed_at: segment.completed_at,
      });
    }

    function endWorkout() {
      if (!self.workout || !self.workoutStartedAt) return;

      self.workoutCompletedAt = nowMs();

      apiCompleteWorkout(
        self.workout.id,
        self.workoutStartedAt,
        self.workoutCompletedAt,
      );

      // Drop any cached paused entry for this template type — workout is done.
      const root = getRoot(self) as any;
      root.workoutSessionStore?.clearPaused?.(
        self.workout.workout_template_type,
      );

      self.cardPhase = CardPhases.FINISHED;
    }

    return {
      _timerTick() {
        if (!self.timerRunning) {
          clearTimer();
          return;
        }

        self.timerRemainingMs = Math.max(self.timerRemainingMs - 1000, 0);

        if (self.timerRemainingMs <= 0) {
          stopTimer();
          console.log('[Timer] Finished, calling nextPhase');
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          nextPhase();
        }
      },

      setWorkout(workout: IWorkoutModel) {
        self.workout = workout;
      },

      startWorkout() {
        if (!self.workout) {
          console.warn('[Store] Cannot start workout: no workout set');
          return;
        }

        self.workoutStartedAt = nowMs();

        // API: mark workout as started
        apiStartWorkout(self.workout.id, self.workoutStartedAt);

        // Start the first segment
        self.currentSegmentIndex = 0;
        self.currentVideoIndex = 0;
        startCurrentSegment();

        // Begin the card phase cycle
        self.cardPhase = CardPhases.DRAW_INTRO;
        // Start paused — user must press play to begin
        self.isPaused = true;
        // Kick off the first phase transition
        deferNextPhase(100);
      },

      loadExistingWorkout(apiWorkout: ApiWorkout) {
        const configData: RemoteConfigData = {
          segments: remoteConfigData.segments as RemoteConfigData['segments'],
        };

        const resolved = resolveWorkout(apiWorkout, configData);
        self.workout = resolved;
        self.workoutStartedAt = resolved.started_at ?? null;
        self.workoutCompletedAt = resolved.completed_at ?? null;

        // Find the first uncompleted, non-skipped segment.
        let resumeIndex = resolved.segments.findIndex(
          (s) => s.completed_at == null && !s.has_skipped,
        );
        if (resumeIndex < 0) {
          resumeIndex = Math.max(resolved.segments.length - 1, 0);
        }
        self.currentSegmentIndex = resumeIndex;
        self.currentVideoIndex = 0;
        self.repCount = 0;
        self.didSkipSegment = false;
        self.cardPhase = CardPhases.DISCARD;
        self.isPaused = false;

        console.log(
          '[Store] Loaded existing workout for resume:',
          resolved.id,
          `(segment ${resumeIndex + 1}/${resolved.segments.length})`,
        );
      },

      resumeWorkout() {
        const segment = self.currentSegment;
        if (!self.workout || !segment) {
          console.warn('[Store] Cannot resume workout: no workout/segment set');
          return;
        }

        // Mark resumed_at on the current segment (analytics parity with mobile).
        apiUpdateSegment(self.workout.id, segment.id, {
          started_at: segment.started_at,
          resumed_at: nowMs(),
        });

        self.cardPhase = CardPhases.DRAW_INTRO;
        self.isPaused = true;
        deferNextPhase(100);
      },

      nextPhase,

      skipStep() {
        if (
          self.cardPhase !== CardPhases.DRAW_ACTIVE &&
          self.cardPhase !== CardPhases.PLAY_ACTIVE
        ) {
          return;
        }
        nextPhase(true);
      },

      completeSingleRep() {
        self.repCount = Math.max(self.repCount - 1, 0);

        if (self.repCount === 0) {
          nextPhase();
        }
      },

      onPlayerEnd() {
        const segment = self.currentSegment;
        if (!segment) return;

        // During draw phase, timer handles progression
        if (self.isDrawStep) return;

        // Rest segments: timer handles it
        if (segment.isRest) return;

        // Non-exercise timed segments: advance on video end
        if (!segment.isExercise) {
          if (self.cardPhase === CardPhases.PLAY_ACTIVE && !self.timerRunning) {
            nextPhase();
          }
          return;
        }

        // Exercise segments: complete one rep per video loop
        if (self.cardPhase === CardPhases.PLAY_ACTIVE) {
          (self as IWorkoutPlayerStore).completeSingleRep();
        }
      },

      togglePause() {
        self.isPaused = !self.isPaused;
        console.log(`[Store] togglePause -> isPaused=${self.isPaused}`);

        if (self.isPaused) {
          pauseTimer();
        } else {
          applyUnpause();
        }
      },

      // Idempotent "begin playing" used at the countdown boundary. Unlike
      // togglePause(), calling this when already playing is a no-op
      startPlayback() {
        if (!self.isPaused) {
          console.warn(
            '[Store] startPlayback: already playing — ignoring redundant call (possible duplicate countdown-complete)',
          );
          return;
        }
        self.isPaused = false;
        console.log('[Store] startPlayback -> isPaused=false');
        applyUnpause();
      },

      cleanup() {
        stopTimer();
      },

      reset() {
        console.log('[Store] Resetting store to initial state');
        stopTimer();
        self.cardPhase = CardPhases.DISCARD;
        self.isPaused = false;
        self.currentSegmentIndex = 0;
        self.currentVideoIndex = 0;
        self.repCount = 0;
        self.didSkipSegment = false;
        self.timerRemainingMs = 0;
        self.timerDurationMs = 0;
        self.timerRunning = false;
        self.workoutStartedAt = null;
        self.workoutCompletedAt = null;
        self.workout = null;
        self.isCreatingWorkout = false;
        self.createError = null;
      },

      createAndLoadWorkout: flow(function* (payload: CreateWorkoutPayload) {
        self.isCreatingWorkout = true;
        self.createError = null;

        try {
          const response = yield apiCreateWorkout(payload);

          if (!response.success) {
            self.createError =
              response.error ??
              "We couldn't prepare your workout. Please try again.";
            return;
          }

          const apiWorkout = response.data.workout;
          const configData: RemoteConfigData = {
            segments: remoteConfigData.segments as RemoteConfigData['segments'],
          };

          self.workout = resolveWorkout(apiWorkout, configData);
          console.log(
            '[Store] Workout created and loaded:',
            self.workout.id,
            `(${self.workout.segments.length} segments)`,
          );
        } catch (err) {
          const message =
            err instanceof Error
              ? err.message
              : "We couldn't prepare your workout. Please try again.";
          self.createError = message;
          console.error('[Store] createAndLoadWorkout failed:', err);
        } finally {
          self.isCreatingWorkout = false;
        }
      }),
    };
  });

export default WorkoutPlayerStore;
